import { useMemo } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';

import { useLatestInfiniteQueryData } from '@hcs/hooks';
import {
  PaginatedResponseData,
  ReportConfigSlugs,
  ReportsAddressesResponseItem,
} from '@hcs/types';
import { ReportSearchInputs } from '@hcs/types';

import { ReportApi } from '../api';

export const QUERY_KEY_REPORTS_ADDRESSES = 'report-api-reports-addresses';

type PageParam = number;

type QueryKey = [
  typeof QUERY_KEY_REPORTS_ADDRESSES,
  'infinite',
  ReportSearchInputs,
];
export const REPORTS_ADDRESSES_DEFAULT_ARGS = {
  activeStatus: 'active' as const,
  pageSize: 1000,
  sortBy: 'orderedDate',
  sortOrder: 'desc',
  reportConfigSlug: [
    ReportConfigSlugs.PexpDynamic,
    ReportConfigSlugs.PexpEffectiveDateDynamic,
  ],
};

const useReportsAddressesInfiniteQuery = (
  reportSearchInputs: ReportSearchInputs,
  options?: Partial<
    UseInfiniteQueryOptions<
      PaginatedResponseData<ReportsAddressesResponseItem[]>,
      unknown,
      InfiniteData<
        PaginatedResponseData<ReportsAddressesResponseItem[]>,
        PageParam
      >,
      PaginatedResponseData<ReportsAddressesResponseItem[]>,
      QueryKey,
      PageParam
    >
  >,
) => {
  const { page: _page, ...inputsWithoutPage } = reportSearchInputs;

  return useInfiniteQuery<
    PaginatedResponseData<ReportsAddressesResponseItem[]>,
    unknown,
    InfiniteData<
      PaginatedResponseData<ReportsAddressesResponseItem[]>,
      PageParam
    >,
    QueryKey,
    PageParam
  >({
    queryKey: [QUERY_KEY_REPORTS_ADDRESSES, 'infinite', inputsWithoutPage],
    queryFn: async ({ pageParam }) => {
      return await ReportApi.fetchReportsAddresses({
        ...inputsWithoutPage,
        page: pageParam,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, numPages } = lastPage.pagination;
      if (page < numPages) {
        return page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      const { page } = firstPage.pagination;
      if (page > 1) {
        return page - 1;
      }
      return undefined;
    },
    ...options,
  });
};

/**
 * Hook to fetch addresses for all reports with a COMPLETED status with pagination using infinite scrolling/lazy loading.
 * Combines all fetched pages into a single list of report addresses.
 */
export const useReportsAddressesInfinite = (
  reportSearchInputs: ReportSearchInputs,
  options?: Partial<
    UseInfiniteQueryOptions<
      PaginatedResponseData<ReportsAddressesResponseItem[]>,
      unknown,
      InfiniteData<
        PaginatedResponseData<ReportsAddressesResponseItem[]>,
        PageParam
      >,
      PaginatedResponseData<ReportsAddressesResponseItem[]>,
      QueryKey,
      PageParam
    >
  >,
) => {
  const queryResult = useLatestInfiniteQueryData(
    useReportsAddressesInfiniteQuery(reportSearchInputs, options),
  );

  // Combine report addresses from each page into a single array
  const reportsAddresses = useMemo(() => {
    if (queryResult.data?.pages) {
      return queryResult.data.pages.reduce<ReportsAddressesResponseItem[]>(
        (allReports, page) => {
          return allReports.concat(page.data);
        },
        [],
      );
    }
    return null;
  }, [queryResult.data]);

  // Get pagination info from the first page (totalCount, numPages)
  const pagination = useMemo(() => {
    if (queryResult.data?.pages?.[0]) {
      const firstPage = queryResult.data.pages[0];
      const lastPage =
        queryResult.data.pages[queryResult.data.pages.length - 1];
      return {
        totalCount: firstPage.pagination.totalCount,
        numPages: firstPage.pagination.numPages,
        pageSize: firstPage.pagination.pageSize,
        currentPage: lastPage?.pagination.page ?? 1,
        pagesLoaded: queryResult.data.pages.length,
      };
    }
    return null;
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: queryResult.data
      ? {
          reportsAddresses,
          pagination,
        }
      : undefined,
  };
};
